import React, { createContext, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { supabase } from '../lib/supabase';
import { useAuth } from './AuthContext';

interface AnalyticsContextType {
  pageVisits: Record<string, number>;
  onlineUsers: number;
  incrementPageVisit: (page: string) => Promise<void>;
  totalVisits: number;
  sessionStartTime: Date;
  getSessionDuration: () => number; // in seconds
  visitTimeline: Array<{ at: string; total: number }>;
  loading: boolean;
}

const AnalyticsContext = createContext<AnalyticsContextType | undefined>(undefined);

const getGuestPresenceKey = () => {
  if (typeof sessionStorage === 'undefined') {
    return `guest_${Math.random().toString(16).slice(2)}_${Date.now()}`;
  }

  const existing = sessionStorage.getItem('guest_presence_key');
  if (existing) return existing;

  const created =
    typeof crypto !== 'undefined' && 'randomUUID' in crypto
      ? crypto.randomUUID()
      : `guest_${Math.random().toString(16).slice(2)}_${Date.now()}`;

  sessionStorage.setItem('guest_presence_key', created);
  return created;
};

export const AnalyticsProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { user } = useAuth();
  const location = useLocation();
  const [pageVisits, setPageVisits] = useState<Record<string, number>>({});
  const [onlineUsers, setOnlineUsers] = useState<number>(0);
  const [totalVisits, setTotalVisits] = useState<number>(0);
  const [sessionStartTime] = useState<Date>(new Date());
  const [visitTimeline, setVisitTimeline] = useState<Array<{ at: string; total: number }>>([]);
  const [loading, setLoading] = useState(false);
  const pageVisitsRef = useRef<Record<string, number>>({});
  const totalVisitsRef = useRef<number>(0);

  useEffect(() => {
    pageVisitsRef.current = pageVisits;
  }, [pageVisits]);

  useEffect(() => {
    totalVisitsRef.current = totalVisits;
  }, [totalVisits]);

  const presenceKey = useMemo(() => user?.id ?? getGuestPresenceKey(), [user?.id]);

  /**
   * Loads analytics data from Supabase (for authenticated users) or localStorage (for guests)
   */
  useEffect(() => {
    const loadAnalytics = async () => {
      if (user) {
        setLoading(true);
        try {
          const { data, error } = await supabase
            .from('page_visits')
            .select('page_path, visit_count')
            .eq('user_id', user.id);

          if (error) throw error;

          if (data) {
            const visits: Record<string, number> = {};
            let total = 0;
            data.forEach((item) => {
              visits[item.page_path] = item.visit_count;
              total += item.visit_count;
            });
            setPageVisits(visits);
            setTotalVisits(total);
          }
        } catch (error) {
          console.error('Error loading analytics from Supabase:', error);
        } finally {
          setLoading(false);
        }
      } else {
        const savedPageVisits = localStorage.getItem('pageVisits');
        const savedTotalVisits = localStorage.getItem('totalVisits');

        if (savedPageVisits) {
          try {
            setPageVisits(JSON.parse(savedPageVisits));
          } catch (e) {
            console.error('Failed to parse page visits', e);
          }
        }

        if (savedTotalVisits) {
          setTotalVisits(parseInt(savedTotalVisits, 10));
        }
      }
    };

    loadAnalytics();
  }, [user]);

  /**
   * Tracks page visits whenever the location changes
   */
  useEffect(() => {
    if (location.pathname) {
      incrementPageVisit(location.pathname);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname]);

  /**
   * Tracks online users via Supabase Realtime presence
   */
  useEffect(() => {
    const channel = supabase.channel('online-users', {
      config: {
        presence: { key: presenceKey }
      }
    });

    channel.on('presence', { event: 'sync' }, () => {
      const state = channel.presenceState();
      setOnlineUsers(Object.keys(state).length);
    });

    channel.subscribe(async (status) => {
      if (status !== 'SUBSCRIBED') return;
      await channel.track({ online_at: new Date().toISOString() });
    });

    return () => {
      supabase.removeChannel(channel);
    };
  }, [presenceKey]);

  /**
   * Increments the visit count for a specific page
   */
  const incrementPageVisit = async (page: string) => {
    const nowIso = new Date().toISOString();
    const currentCount = pageVisitsRef.current[page] || 0;
    const nextCount = currentCount + 1;
    const nextTotal = totalVisitsRef.current + 1;

    setPageVisits((prev) => ({ ...prev, [page]: nextCount }));
    setTotalVisits(nextTotal);
    setVisitTimeline((prev) => {
      const next = [...prev, { at: nowIso, total: nextTotal }];
      return next.length > 60 ? next.slice(next.length - 60) : next;
    });

    pageVisitsRef.current = { ...pageVisitsRef.current, [page]: nextCount };
    totalVisitsRef.current = nextTotal;

    if (user) {
      try {
        // Upsert into Supabase: if exists, increment; if not, insert.
        // PostgreSQL's ON CONFLICT is used via Supabase's upsert
        const { error } = await supabase
          .from('page_visits')
          .upsert({ 
            user_id: user.id, 
            page_path: page, 
            visit_count: nextCount,
            last_visited_at: nowIso
          }, { onConflict: 'user_id,page_path' });

        if (error) throw error;
      } catch (error) {
        console.error('Error saving analytics to Supabase:', error);
      }
    } else {
      // Save to localStorage for guests
      localStorage.setItem('pageVisits', JSON.stringify(pageVisitsRef.current));
      localStorage.setItem('totalVisits', nextTotal.toString());
    }
  };

  /**
   * Calculates the current session duration in seconds
   */
  const getSessionDuration = () => {
    const now = new Date();
    return Math.floor((now.getTime() - sessionStartTime.getTime()) / 1000);
  };

  return (
    <AnalyticsContext.Provider
      value={{
        pageVisits,
        onlineUsers,
        incrementPageVisit,
        totalVisits,
        sessionStartTime,
        getSessionDuration,
        visitTimeline,
        loading
      }}
    >
      {children}
    </AnalyticsContext.Provider>
  );
};

/**
 * Custom hook to access the analytics context
 * @returns Analytics context with tracking functions and metrics
 * @throws Error if used outside of AnalyticsProvider
 */
export const useAnalytics = () => {
  const context = useContext(AnalyticsContext);
  if (context === undefined) {
    throw new Error('useAnalytics must be used within an AnalyticsProvider');
  }
  return context;
};
